import {
  type QueryClient,
  queryOptions,
  skipToken,
} from "@tanstack/react-query"
import { adjustVirtualTime } from "@myvp/shared/src/clock/virtual-clock"
import {
  defaultClockApi,
  type ClockApi,
} from "@myvp/shared/src/clock/clock-api"
import { type Role } from "@myvp/todos/src/gql/graphql"
import * as userVirtualTimeService from "@myvp/todos/src/uat-time-travel/user-virtual-time.service"

export interface VirtualTimeStudyRoleContext {
  studyId: string
  role: Role
  participantRelatedUserId: string
}

export const getUserVirtualTimeQueryOptions = (
  studyRoleContext: VirtualTimeStudyRoleContext | undefined
) => {
  return queryOptions({
    queryKey: ["getUserVirtualTime", studyRoleContext],
    queryFn: async () => {
      if (!studyRoleContext) {
        return skipToken
      }
      const before = performance.now()
      const result =
        await userVirtualTimeService.getUserVirtualTime(studyRoleContext)
      const after = performance.now()
      if (!result.virtualTime) {
        return { virtualTime: null }
      }
      const virtualNow = adjustVirtualTime({
        ...result.virtualTime,
        rtt: after - before,
      })
      return {
        virtualTime: {
          virtualNow,
          lastUpdatedAt: after,
          paused: result.virtualTime.paused,
        },
      }
    },
  })
}

export const createUserVirtualClockApi = (
  queryClient: QueryClient,
  args: VirtualTimeStudyRoleContext | undefined
): ClockApi => {
  if (!args) {
    return defaultClockApi()
  }
  const queryOptions = getUserVirtualTimeQueryOptions(args)
  return {
    id: args,
    readData: () => {
      const data = queryClient.getQueryData(queryOptions.queryKey)
      if (typeof data === "symbol" || !data) {
        return data
      } else if (!data?.virtualTime) {
        return skipToken
      } else {
        return data.virtualTime.virtualNow
      }
    },
    fetchData: async () => {
      const data = await queryClient.ensureQueryData({
        ...queryOptions,
        staleTime: Infinity,
      })
      if (typeof data === "symbol" || !data?.virtualTime) {
        return skipToken
      } else {
        return data.virtualTime.virtualNow
      }
    },
  }
}
